import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageShell from "../components/layout/PageShell";
import {
    getPrograms,
    createProgram,
    updateProgram,
    deleteProgram
} from "../services/crmService";

import "./project.css";

const createEmptyForm = () => ({
    name: "",
    subtitle: "",
    description: "",
    badge: "",
    category: "",
    audience: "",
    level: "",
    imageUrl: "",
    keyPoints: [""],
    isPublished: true,
    sortOrder: 0
});

function getErrorMessage(error, fallback = "Something went wrong.") {
    const apiError = error?.response?.data;

    if (typeof apiError === "string") return apiError;
    if (apiError?.message) return apiError.message;
    if (apiError?.title) return apiError.title;
    if (error?.message) return error.message;

    return fallback;
}

export default function CrmProjectProgramsPage() {
    const { projectId } = useParams();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(createEmptyForm());
    const [searchText, setSearchText] = useState("");

    const pageTitle = useMemo(() => "Programs", []);
    const publishedCount = useMemo(() => items.filter((x) => x.isPublished).length, [items]);

    const filteredItems = useMemo(() => {
        const query = searchText.trim().toLowerCase();

        if (!query) return items;

        return items.filter((item) => {
            const text = [
                item.name,
                item.subtitle,
                item.description,
                item.badge,
                item.category,
                item.audience,
                item.level,
                item.isPublished ? "published" : "draft"
            ].filter(Boolean).join(" ").toLowerCase();

            return text.includes(query);
        });
    }, [items, searchText]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError("");

            const data = await getPrograms(projectId);
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError(getErrorMessage(err, "Failed to load programs."));
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        load();
    }, [load]);

    const resetForm = () => {
        setEditingId(null);
        setForm(createEmptyForm());
    };

    const updateField = (name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const updateKeyPoint = (index, value) => {
        setForm((prev) => ({
            ...prev,
            keyPoints: prev.keyPoints.map((item, i) => (i === index ? value : item))
        }));
    };

    const addKeyPoint = () => {
        setForm((prev) => ({
            ...prev,
            keyPoints: [...prev.keyPoints, ""]
        }));
    };

    const removeKeyPoint = (index) => {
        setForm((prev) => ({
            ...prev,
            keyPoints:
                prev.keyPoints.length <= 1
                    ? [""]
                    : prev.keyPoints.filter((_, i) => i !== index)
        }));
    };

    const startCreate = () => {
        resetForm();
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        setForm({
            name: item.name || "",
            subtitle: item.subtitle || "",
            description: item.description || "",
            badge: item.badge || "",
            category: item.category || "",
            audience: item.audience || "",
            level: item.level || "",
            imageUrl: item.imageUrl || "",
            keyPoints: Array.isArray(item.keyPoints) && item.keyPoints.length > 0 ? item.keyPoints : [""],
            isPublished: !!item.isPublished,
            sortOrder: item.sortOrder || 0
        });

        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!form.name.trim()) {
            toast.error("Program name is required.");
            return;
        }

        const payload = {
            ...form,
            name: form.name.trim(),
            subtitle: form.subtitle.trim(),
            description: form.description.trim(),
            badge: form.badge.trim(),
            category: form.category.trim(),
            audience: form.audience.trim(),
            level: form.level.trim(),
            imageUrl: form.imageUrl.trim(),
            keyPoints: form.keyPoints.map((x) => x.trim()).filter(Boolean),
            sortOrder: Number(form.sortOrder || 0)
        };

        try {
            setSaving(true);

            if (editingId) {
                await updateProgram(projectId, editingId, payload);
                toast.success("Program updated successfully.");
            } else {
                await createProgram(projectId, payload);
                toast.success("Program created successfully.");
            }

            resetForm();
            await load();
        } catch (err) {
            console.error(err);
            toast.error(getErrorMessage(err, "Failed to save program."));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (programId) => {
        const confirmed = window.confirm("Delete this program?");
        if (!confirmed) return;

        try {
            await deleteProgram(projectId, programId);
            toast.success("Program deleted.");
            await load();

            if (editingId === programId) {
                resetForm();
            }
        } catch (err) {
            console.error(err);
            toast.error(getErrorMessage(err, "Failed to delete program."));
        }
    };

    return (
        <PageShell title={pageTitle}>
            <div className="programs-page-shell">
                <section className="module-hero-premium">
                    <div>
                        <span className="project-kicker">Program Studio</span>
                        <h1>Project Programs</h1>
                        <p>
                            Manage academy, coaching, training, fitness, or course programs linked
                            to this generated website and CRM module.
                        </p>

                        <div className="module-hero-meta">
                            <span>{items.length} programs</span>
                            <span>{publishedCount} published</span>
                            <span>{items.length - publishedCount} draft</span>
                        </div>
                    </div>

                    <div className="module-hero-actions">
                        <Link className="project-action-btn secondary" to={`/crm/projects/${projectId}`}>
                            Back to Project
                        </Link>

                        <button
                            type="button"
                            className="project-action-btn primary"
                            onClick={startCreate}
                        >
                            Add Program
                        </button>
                    </div>
                </section>

                <section className="programs-layout-grid">
                    <div className="project-panel">
                        <div className="project-panel-header">
                            <div>
                                <span className="project-kicker">{editingId ? "Edit Program" : "Create Program"}</span>
                                <h2>{editingId ? "Update Program Content" : "Add New Program"}</h2>
                                <p>
                                    Content saved here can be used by your generated programs page,
                                    detail pages, CTAs, and CRM lead flow.
                                </p>
                            </div>
                        </div>

                        <form className="project-form-grid" onSubmit={handleSubmit}>
                            <div className="project-form-fields two">
                                <label>
                                    <span>Name</span>
                                    <input
                                        value={form.name}
                                        onChange={(event) => updateField("name", event.target.value)}
                                        placeholder="Elite Football Program"
                                    />
                                </label>

                                <label>
                                    <span>Subtitle</span>
                                    <input
                                        value={form.subtitle}
                                        onChange={(event) => updateField("subtitle", event.target.value)}
                                        placeholder="Structured training for serious players"
                                    />
                                </label>

                                <label>
                                    <span>Badge</span>
                                    <input
                                        value={form.badge}
                                        onChange={(event) => updateField("badge", event.target.value)}
                                        placeholder="Popular"
                                    />
                                </label>

                                <label>
                                    <span>Category</span>
                                    <input
                                        value={form.category}
                                        onChange={(event) => updateField("category", event.target.value)}
                                        placeholder="Youth Academy"
                                    />
                                </label>

                                <label>
                                    <span>Audience</span>
                                    <input
                                        value={form.audience}
                                        onChange={(event) => updateField("audience", event.target.value)}
                                        placeholder="Ages 8-14"
                                    />
                                </label>

                                <label>
                                    <span>Level</span>
                                    <input
                                        value={form.level}
                                        onChange={(event) => updateField("level", event.target.value)}
                                        placeholder="Beginner to Intermediate"
                                    />
                                </label>

                                <label>
                                    <span>Image URL</span>
                                    <input
                                        value={form.imageUrl}
                                        onChange={(event) => updateField("imageUrl", event.target.value)}
                                        placeholder="https://..."
                                    />
                                </label>

                                <label>
                                    <span>Sort Order</span>
                                    <input
                                        type="number"
                                        value={form.sortOrder}
                                        onChange={(event) => updateField("sortOrder", event.target.value)}
                                    />
                                </label>
                            </div>

                            <label>
                                <span>Description</span>
                                <textarea
                                    rows={5}
                                    value={form.description}
                                    onChange={(event) => updateField("description", event.target.value)}
                                    placeholder="Describe this program..."
                                />
                            </label>

                            <div className="program-keypoints-box">
                                <div className="program-keypoints-head">
                                    <span>Key Points</span>
                                    <button
                                        type="button"
                                        className="project-action-btn compact secondary"
                                        onClick={addKeyPoint}
                                    >
                                        Add Key Point
                                    </button>
                                </div>

                                <div className="program-keypoints-list">
                                    {form.keyPoints.map((point, index) => (
                                        <div className="program-keypoint-row" key={`point-${index}`}>
                                            <input
                                                value={point}
                                                onChange={(event) => updateKeyPoint(index, event.target.value)}
                                                placeholder={`Key point ${index + 1}`}
                                            />

                                            <button
                                                type="button"
                                                className="project-action-btn compact secondary"
                                                onClick={() => removeKeyPoint(index)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <label className="project-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={form.isPublished}
                                    onChange={(event) => updateField("isPublished", event.target.checked)}
                                />
                                <span>Published</span>
                            </label>

                            <div className="project-form-actions">
                                <button type="submit" className="project-action-btn primary" disabled={saving}>
                                    {saving ? "Saving..." : editingId ? "Update Program" : "Create Program"}
                                </button>

                                <button
                                    type="button"
                                    className="project-action-btn secondary"
                                    onClick={resetForm}
                                    disabled={saving}
                                >
                                    Reset
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="project-panel">
                        <div className="project-panel-header">
                            <div>
                                <span className="project-kicker">Program Library</span>
                                <h2>Program List</h2>
                                <p>
                                    Search and edit existing programs. Published programs can appear
                                    on generated website pages.
                                </p>
                            </div>
                        </div>

                        <div className="project-search-box full">
                            <span>Search</span>
                            <input
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                placeholder="Search programs, category, audience, level..."
                            />
                        </div>

                        {loading ? (
                            <div className="project-loading-card">Loading programs...</div>
                        ) : error ? (
                            <div className="projects-error-card">
                                <strong>Could not load programs</strong>
                                <p>{error}</p>
                            </div>
                        ) : filteredItems.length === 0 ? (
                            <div className="project-empty">
                                {items.length === 0
                                    ? "No programs added yet."
                                    : "No programs match your search."}
                            </div>
                        ) : (
                            <div className="program-list-polished">
                                {filteredItems.map((item) => (
                                    <article key={item.id} className="program-card-polished">
                                        <div>
                                            <div className="program-card-head">
                                                <h3>{item.name}</h3>

                                                <div className="program-card-pills">
                                                    {item.badge ? <span>{item.badge}</span> : null}
                                                    <span>{item.isPublished ? "Published" : "Draft"}</span>
                                                </div>
                                            </div>

                                            {item.subtitle ? <p>{item.subtitle}</p> : null}
                                            {item.description ? <small>{item.description}</small> : null}

                                            <div className="program-meta-row">
                                                {item.category ? <span>{item.category}</span> : null}
                                                {item.audience ? <span>{item.audience}</span> : null}
                                                {item.level ? <span>{item.level}</span> : null}
                                                <span>Sort: {item.sortOrder || 0}</span>
                                            </div>
                                        </div>

                                        <div className="program-card-actions">
                                            <button
                                                type="button"
                                                className="project-action-btn compact secondary"
                                                onClick={() => startEdit(item)}
                                            >
                                                Edit
                                            </button>

                                            <button
                                                type="button"
                                                className="project-action-btn compact danger"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </PageShell>
    );
}